import { useState, useEffect, useCallback, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useLang } from "../lang";

/**
 * ImageLightbox — full-screen image viewer with zoom, pan, and keyboard navigation.
 *
 * Props:
 *   - images: array of image URLs
 *   - startIndex: initial image index (default 0)
 *   - open: boolean to show/hide the lightbox
 *   - onClose: callback when user closes the lightbox
 */
const MAX_SCALE = 6;
const clampScale = (s) => Math.max(1, Math.min(MAX_SCALE, s));

export default function ImageLightbox({ images, startIndex = 0, open, onClose }) {
  const [index, setIndex] = useState(startIndex);
  const [view, setView] = useState({ s: 1, x: 0, y: 0 });
  const [dragging, setDragging] = useState(false);
  const stageRef = useRef(null);
  const imgRef = useRef(null);
  const pointers = useRef(new Map());
  const gesture = useRef(null);
  const lastTap = useRef(0);
  const scale = view.s;
  const { lang } = useLang();

  const clampView = useCallback((v) => {
    const el = imgRef.current;
    if (!el || v.s <= 1) return { s: 1, x: 0, y: 0 };
    const mx = Math.max(0, (el.offsetWidth * v.s - window.innerWidth) / 2 + 40);
    const my = Math.max(0, (el.offsetHeight * v.s - window.innerHeight) / 2 + 40);
    return { s: v.s, x: Math.max(-mx, Math.min(mx, v.x)), y: Math.max(-my, Math.min(my, v.y)) };
  }, []);

  // Zoom keeping the point (px, py) — relative to stage centre — fixed on screen
  const zoomAt = useCallback((nextScale, px = 0, py = 0) => {
    setView((v) => {
      const s = clampScale(nextScale);
      const k = s / v.s;
      return clampView({ s, x: px - k * (px - v.x), y: py - k * (py - v.y) });
    });
  }, [clampView]);

  const toCentre = (cx, cy) => {
    const r = stageRef.current.getBoundingClientRect();
    return [cx - r.left - r.width / 2, cy - r.top - r.height / 2];
  };

  const resetTransform = useCallback(() => setView({ s: 1, x: 0, y: 0 }), []);

  useEffect(() => {
    if (open) {
      setIndex(startIndex);
      resetTransform();
    }
  }, [open, startIndex, resetTransform]);

  useEffect(() => {
    if (open) {
      const prev = document.body.style.overflow;
      document.body.style.overflow = "hidden";
      return () => { document.body.style.overflow = prev; };
    }
  }, [open]);

  const next = useCallback(() => {
    setIndex((i) => (i + 1) % images.length);
    resetTransform();
  }, [images.length, resetTransform]);
  const prev = useCallback(() => {
    setIndex((i) => (i - 1 + images.length) % images.length);
    resetTransform();
  }, [images.length, resetTransform]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e) => {
      if (e.key === "Escape") onClose();
      else if (e.key === "ArrowRight") next();
      else if (e.key === "ArrowLeft") prev();
      else if (e.key === "+" || e.key === "=") zoomAt(scale + 0.5);
      else if (e.key === "-") zoomAt(scale - 0.5);
      else if (e.key === "0") resetTransform();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, next, prev, onClose, resetTransform, zoomAt, scale]);

  // Non-passive wheel listener so the page never scrolls behind the viewer
  useEffect(() => {
    const el = stageRef.current;
    if (!open || !el) return;
    const onWheel = (e) => {
      e.preventDefault();
      const [px, py] = toCentre(e.clientX, e.clientY);
      setView((v) => {
        const s = clampScale(v.s * Math.exp(-e.deltaY * 0.0025));
        const k = s / v.s;
        return clampView({ s, x: px - k * (px - v.x), y: py - k * (py - v.y) });
      });
    };
    el.addEventListener("wheel", onWheel, { passive: false });
    return () => el.removeEventListener("wheel", onWheel);
  }, [open, clampView]);

  const startGesture = () => {
    const pts = [...pointers.current.values()];
    if (pts.length >= 2) {
      const [a, b] = pts;
      gesture.current = {
        type: "pinch",
        dist: Math.hypot(a.x - b.x, a.y - b.y),
        mid: toCentre((a.x + b.x) / 2, (a.y + b.y) / 2),
        view,
      };
    } else if (pts.length === 1) {
      gesture.current = { type: "pan", start: pts[0], view };
    }
  };

  const onPointerDown = (e) => {
    if (e.target.closest("button")) return;
    e.currentTarget.setPointerCapture(e.pointerId);
    pointers.current.set(e.pointerId, { x: e.clientX, y: e.clientY, onImg: e.target === imgRef.current });
    startGesture();
    setDragging(true);
  };

  const onPointerMove = (e) => {
    if (!pointers.current.has(e.pointerId)) return;
    pointers.current.set(e.pointerId, { ...pointers.current.get(e.pointerId), x: e.clientX, y: e.clientY });
    const g = gesture.current;
    if (!g) return;
    const pts = [...pointers.current.values()];
    if (g.type === "pinch" && pts.length >= 2) {
      const [a, b] = pts;
      const s = clampScale(g.view.s * (Math.hypot(a.x - b.x, a.y - b.y) / g.dist));
      const mid = toCentre((a.x + b.x) / 2, (a.y + b.y) / 2);
      const k = s / g.view.s;
      setView(clampView({
        s,
        x: mid[0] - k * (g.mid[0] - g.view.x),
        y: mid[1] - k * (g.mid[1] - g.view.y),
      }));
    } else if (g.type === "pan" && g.view.s > 1) {
      setView(clampView({ s: g.view.s, x: g.view.x + e.clientX - g.start.x, y: g.view.y + e.clientY - g.start.y }));
    }
  };

  const onPointerUp = (e) => {
    const g = gesture.current;
    const p = pointers.current.get(e.pointerId);
    pointers.current.delete(e.pointerId);
    if (pointers.current.size > 0) { startGesture(); return; }
    gesture.current = null;
    setDragging(false);
    if (!g || g.type !== "pan" || !p) return;
    const dx = p.x - g.start.x;
    const dy = p.y - g.start.y;
    const moved = Math.hypot(dx, dy) > 10;
    // Swipe between images when not zoomed
    if (moved && g.view.s === 1 && images.length > 1 && Math.abs(dx) > 60 && Math.abs(dx) > Math.abs(dy)) {
      dx < 0 ? next() : prev();
      return;
    }
    // Double tap / double click: zoom on the tapped point or reset
    if (!moved && g.start.onImg) {
      const now = Date.now();
      if (now - lastTap.current < 300) {
        lastTap.current = 0;
        if (view.s > 1) resetTransform();
        else zoomAt(2.5, ...toCentre(p.x, p.y));
      } else lastTap.current = now;
    }
  };

  if (!open || !images || images.length === 0) return null;

  return (
    <AnimatePresence>
      <motion.div
        key="lightbox"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        transition={{ duration: 0.25 }}
        className="fixed inset-0 z-[120] bg-black/95 flex items-center justify-center select-none"
        data-testid="image-lightbox"
      >
        {/* Close */}
        <button
          type="button"
          onClick={onClose}
          aria-label="close"
          data-testid="lightbox-close"
          className="absolute top-4 right-4 md:top-6 md:right-6 z-10 w-11 h-11 rounded-full bg-black/55 backdrop-blur-md ring-1 ring-white/15 hover:bg-black/70 text-white flex items-center justify-center transition-colors"
        >
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
            <line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" />
          </svg>
        </button>

        {/* Zoom controls */}
        <div className="absolute bottom-6 left-1/2 -translate-x-1/2 z-10 flex items-center gap-2 bg-black/55 backdrop-blur-md ring-1 ring-white/15 text-white rounded-full px-2 py-1.5">
          <button type="button" onClick={() => zoomAt(scale - 0.5)} aria-label="zoom out" data-testid="lightbox-zoom-out" className="w-8 h-8 flex items-center justify-center rounded-full hover:bg-white/10">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><line x1="5" y1="12" x2="19" y2="12" /></svg>
          </button>
          <span className="text-[12px] tabular-nums min-w-[52px] text-center">{Math.round(scale * 100)}%</span>
          <button type="button" onClick={() => zoomAt(scale + 0.5)} aria-label="zoom in" data-testid="lightbox-zoom-in" className="w-8 h-8 flex items-center justify-center rounded-full hover:bg-white/10">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><line x1="12" y1="5" x2="12" y2="19" /><line x1="5" y1="12" x2="19" y2="12" /></svg>
          </button>
          {scale !== 1 && (
            <button type="button" onClick={resetTransform} className="ml-1 text-[11px] px-2 py-1 rounded-full hover:bg-white/10" data-testid="lightbox-reset">Reset</button>
          )}
        </div>

        {/* Counter */}
        {images.length > 1 && (
          <div className="absolute top-4 left-1/2 -translate-x-1/2 z-10 px-3 py-1.5 rounded-full bg-black/55 backdrop-blur-md ring-1 ring-white/15 text-white text-[12px] tabular-nums" data-testid="lightbox-counter">
            {index + 1} / {images.length}
          </div>
        )}

        {/* Prev / Next */}
        {images.length > 1 && (
          <>
            <button
              type="button"
              onClick={prev}
              aria-label="previous"
              data-testid="lightbox-prev"
              className="absolute left-3 md:left-6 top-1/2 -translate-y-1/2 z-10 w-11 h-11 md:w-12 md:h-12 rounded-full bg-black/55 backdrop-blur-md ring-1 ring-white/15 hover:bg-black/70 text-white flex items-center justify-center transition-colors"
            >
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><polyline points="15 18 9 12 15 6" /></svg>
            </button>
            <button
              type="button"
              onClick={next}
              aria-label="next"
              data-testid="lightbox-next"
              className="absolute right-3 md:right-6 top-1/2 -translate-y-1/2 z-10 w-11 h-11 md:w-12 md:h-12 rounded-full bg-black/55 backdrop-blur-md ring-1 ring-white/15 hover:bg-black/70 text-white flex items-center justify-center transition-colors"
            >
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><polyline points="9 18 15 12 9 6" /></svg>
            </button>
          </>
        )}

        {/* Zoom hint */}
        {scale === 1 && (
          <div className="absolute bottom-[72px] left-1/2 -translate-x-1/2 z-10 text-white/60 text-[12px] whitespace-nowrap pointer-events-none" data-testid="lightbox-zoom-hint">
            <span className="md:hidden">{lang === "it" ? "Pizzica o tocca due volte per ingrandire" : "Pinch or double-tap to zoom"}</span>
            <span className="hidden md:inline">{lang === "it" ? "Rotella o doppio clic per ingrandire · trascina per spostarti" : "Scroll or double-click to zoom · drag to pan"}</span>
          </div>
        )}

        {/* Stage */}
        <div
          ref={stageRef}
          className="absolute inset-0 flex items-center justify-center overflow-hidden"
          style={{ touchAction: "none" }}
          onPointerDown={onPointerDown}
          onPointerMove={onPointerMove}
          onPointerUp={onPointerUp}
          onPointerCancel={onPointerUp}
          data-testid="lightbox-stage"
        >
          <motion.div
            key={index}
            initial={{ opacity: 0, scale: 0.96 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.25 }}
          >
            <img
              ref={imgRef}
              src={images[index]}
              alt=""
              draggable={false}
              data-testid="lightbox-image"
              style={{
                transform: `translate3d(${view.x}px, ${view.y}px, 0) scale(${view.s})`,
                cursor: scale > 1 ? (dragging ? "grabbing" : "grab") : "zoom-in",
                transition: dragging ? "none" : "transform 0.2s ease",
              }}
              className="block max-w-[92vw] max-h-[86vh] object-contain will-change-transform"
            />
          </motion.div>
        </div>
      </motion.div>
    </AnimatePresence>
  );
}
